import React, { useState, useEffect } from 'react';
import Modal from '../Modal';
import { Fournisseur } from '../../types/weighing';

interface EditSupplierModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (supplier: Partial<Fournisseur>) => void;
  supplier: Fournisseur;
}

const EditSupplierModal: React.FC<EditSupplierModalProps> = ({ 
  isOpen, 
  onClose, 
  onSave, 
  supplier 
}) => {
  const [formData, setFormData] = useState<Partial<Fournisseur>>({
    code_supplier: '',
    nom: '',
    adresse: '',
    contact: '',
    email: '',
    cooperative: '',
    certifications: []
  });

  const [errors, setErrors] = useState<Partial<Fournisseur>>({});
  const [certificationInput, setCertificationInput] = useState('');

  useEffect(() => {
    if (supplier) {
      setFormData({
        code_supplier: supplier.code_supplier || '',
        nom: supplier.nom || '',
        adresse: supplier.adresse || '',
        contact: supplier.contact || '',
        email: supplier.email || '',
        cooperative: supplier.cooperative || '',
        certifications: supplier.certifications || []
      });
    }
  }, [supplier]);

  const handleInputChange = (field: keyof Fournisseur, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const addCertification = () => {
    if (certificationInput.trim()) {
      setFormData(prev => ({
        ...prev,
        certifications: [...(prev.certifications || []), certificationInput.trim()]
      }));
      setCertificationInput('');
    }
  };

  const removeCertification = (index: number) => {
    setFormData(prev => ({
      ...prev,
      certifications: prev.certifications?.filter((_, i) => i !== index) || []
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Fournisseur> = {};

    if (!formData.code_supplier?.trim()) {
      newErrors.code_supplier = 'Le code fournisseur est requis';
    } else if (!/^[A-Z0-9\-_]+$/.test(formData.code_supplier)) {
      newErrors.code_supplier = 'Le code fournisseur ne peut contenir que des lettres, chiffres, tirets (-) et underscores (_)';
    } else if (formData.code_supplier.length < 2) {
      newErrors.code_supplier = 'Le code fournisseur doit contenir au moins 2 caractères';
    }
    if (!formData.nom?.trim()) {
      newErrors.nom = 'Le nom est requis';
    }
    // Les champs adresse, contact et cooperative sont maintenant optionnels

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      onSave(formData);
      onClose();
      setErrors({});
    }
  };

  const handleClose = () => {
    setErrors({});
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Modifier le fournisseur" size="lg">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Code Fournisseur */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Code Fournisseur <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.code_supplier || ''}
            onChange={(e) => handleInputChange('code_supplier', e.target.value.toUpperCase())}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
              errors.code_supplier ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="SUP-001 ou SUP_001"
          />
          {errors.code_supplier && (
            <p className="mt-1 text-sm text-red-600">{errors.code_supplier}</p>
          )}
        </div>

        {/* Nom et Contact */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nom <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.nom || ''}
              onChange={(e) => handleInputChange('nom', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                errors.nom ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Nom du fournisseur"
            />
            {errors.nom && (
              <p className="mt-1 text-sm text-red-600">{errors.nom}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Contact
            </label>
            <input
              type="text"
              value={formData.contact || ''}
              onChange={(e) => handleInputChange('contact', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                errors.contact ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Nom du contact principal"
            />
            {errors.contact && (
              <p className="mt-1 text-sm text-red-600">{errors.contact}</p>
            )}
          </div>
        </div>

        {/* Coopérative */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Coopérative
          </label>
          <input
            type="text"
            value={formData.cooperative || ''}
            onChange={(e) => handleInputChange('cooperative', e.target.value)}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
              errors.cooperative ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="Nom de la coopérative"
          />
          {errors.cooperative && (
            <p className="mt-1 text-sm text-red-600">{errors.cooperative}</p>
          )}
        </div>

        {/* Adresse */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Adresse
          </label>
          <textarea
            value={formData.adresse || ''}
            onChange={(e) => handleInputChange('adresse', e.target.value)}
            rows={3}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
              errors.adresse ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="Adresse complète du fournisseur"
          />
          {errors.adresse && (
            <p className="mt-1 text-sm text-red-600">{errors.adresse}</p>
          )}
        </div>

        {/* Email */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Email
          </label>
          <input
            type="email"
            value={formData.email || ''}
            onChange={(e) => handleInputChange('email', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            placeholder="fournisseur@email.com"
          />
        </div>

        {/* Certifications */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Certifications
          </label>
          <div className="space-y-2">
            <div className="flex gap-2">
              <input
                type="text"
                value={certificationInput}
                onChange={(e) => setCertificationInput(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                placeholder="Ajouter une certification"
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCertification())}
              />
              <button
                type="button"
                onClick={addCertification}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Ajouter
              </button>
            </div>
            {formData.certifications && formData.certifications.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {formData.certifications.map((cert, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
                  >
                    {cert}
                    <button
                      type="button"
                      onClick={() => removeCertification(index)}
                      className="ml-2 text-blue-600 hover:text-blue-800"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Boutons d'action */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Annuler
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
          >
            Modifier le fournisseur
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default EditSupplierModal;
